import { createContext, useState, useEffect, useContext } from 'react';
import { getPersonalizedPrompts } from '../services/diagramApi';

// Default persona
const DEFAULT_PERSONA = {
  job_title: 'Software Engineer',
  industry: 'Technology',
  experience_level: 'Intermediate',
  use_case: 'Technical Documentation'
};

// Create context
const PersonaContext = createContext();

// Custom hook for using the persona context
export const usePersona = () => {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within a PersonaProvider');
  }
  return context;
};

// Provider component
export const PersonaProvider = ({ children }) => {
  const [persona, setPersona] = useState(DEFAULT_PERSONA);
  const [personalizedPrompts, setPersonalizedPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shouldFetchPrompts, setShouldFetchPrompts] = useState(false);
  const [promptsJustLoaded, setPromptsJustLoaded] = useState(false);

  // Load persona from localStorage on component mount
  useEffect(() => {
    const loadPersona = () => {
      try {
        const storedPersona = localStorage.getItem('mermaid-flow-persona');
        if (storedPersona) {
          setPersona(JSON.parse(storedPersona));
        }
      } catch (err) {
        console.error('Error loading persona:', err);
        // Fallback to default persona
        setPersona(DEFAULT_PERSONA);
      }
    };

    loadPersona();
  }, []);

  // Save persona to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('mermaid-flow-persona', JSON.stringify(persona));
    } catch (err) {
      console.error('Error saving persona:', err);
    }
  }, [persona]);

  // Fetch personalized prompts only when explicitly requested
  useEffect(() => {
    if (!shouldFetchPrompts) return;
    
    const fetchPrompts = async () => {
      setIsLoading(true);
      setError(null);
      setPromptsJustLoaded(false);
      
      try {
        const response = await getPersonalizedPrompts(persona);
        setPersonalizedPrompts(response.prompts);
        setPromptsJustLoaded(true);
        
        // Reset the promptsJustLoaded flag after 3 seconds
        setTimeout(() => {
          setPromptsJustLoaded(false);
        }, 3000);
      } catch (err) {
        console.error('Error fetching personalized prompts:', err);
        setError('Failed to fetch personalized prompts');
        // Reset to empty prompts on error
        setPersonalizedPrompts([]);
      } finally {
        setIsLoading(false);
        setShouldFetchPrompts(false);
      }
    };

    fetchPrompts();
  }, [shouldFetchPrompts, persona]);

  // Update persona and trigger prompt fetch
  const updatePersona = (newPersona) => {
    setPersona((prevPersona) => ({
      ...prevPersona,
      ...newPersona,
    }));
    setShouldFetchPrompts(true);
  };

  // Reset persona to default and trigger fetch of default personalized prompts
  const resetPersona = () => {
    setPersona(DEFAULT_PERSONA);
    setShouldFetchPrompts(true);
  };

  // Context value
  const value = {
    persona,
    updatePersona,
    resetPersona,
    personalizedPrompts,
    isLoading,
    error,
    promptsJustLoaded
  };

  return (
    <PersonaContext.Provider value={value}>
      {children}
    </PersonaContext.Provider>
  );
};

export default PersonaContext; 